import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, MapPin } from "lucide-react";
import { getHeaderPhotos, submitHeaderPhoto } from "../lib/api";
import ImageUpload from "../components/ImageUpload";

const empty = { image_url: "", photographer: "", email: "", location: "", caption: "", credit_link: "", consent: false };
const input = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[var(--gold)]";

export default function PhotoCredits() {
  const [photos, setPhotos] = useState([]);
  useEffect(() => { getHeaderPhotos().then(setPhotos).catch(() => {}); }, []);
  return (
    <div className="mx-auto max-w-6xl px-4 py-10" data-testid="photo-credits-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--gold)]">Nassau Life</p>
      <h1 className="font-display mt-1 text-4xl font-black text-[var(--navy)] sm:text-5xl">Photo of the Day</h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base">Our portal header features a different local photograph each day. Thank you to the photographers who share our coast with us.</p>
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {photos.map((p) => <CreditCard key={p.id} p={p} />)}
      </div>
      <SubmitPhotoForm />
    </div>
  );
}

function CreditCard({ p }) {
  return (
    <figure data-testid={`photo-credit-${p.id}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <img src={p.image_url} alt={p.caption} className="h-52 w-full object-cover" />
      <figcaption className="p-4">
        {p.caption && <p className="font-semibold text-[var(--navy)]">{p.caption}</p>}
        <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-600"><Camera size={14} />
          {p.credit_link ? <a href={p.credit_link} target="_blank" rel="noreferrer" className="underline hover:text-[var(--navy)]">{p.photographer}</a> : p.photographer}
        </p>
        {p.location && <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-400"><MapPin size={12} /> {p.location}</p>}
      </figcaption>
    </figure>
  );
}

function SubmitPhotoForm() {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_url || !form.photographer.trim()) return toast.error("Please add a photo and your name.");
    if (!form.consent) return toast.error("Please confirm you took the photo and allow us to publish it.");
    setBusy(true);
    try { const r = await submitHeaderPhoto(form); toast.success(r.message); setForm(empty); }
    catch { toast.error("Could not submit your photo."); }
    finally { setBusy(false); }
  };
  return (
    <form id="submit" onSubmit={submit} data-testid="submit-photo-form" className="mt-12 scroll-mt-24 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
      <h2 className="font-display text-2xl font-bold text-[var(--navy)]">Submit your photo</h2>
      <p className="mt-1 text-sm text-slate-500">Landscape photos of Nassau County and Amelia Island work best. Editors review every submission.</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><ImageUpload value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} testid="submit-photo-upload" camera={false} /></div>
        <input data-testid="submit-photo-name" placeholder="Your name (as it should appear in the credit)" value={form.photographer} onChange={set("photographer")} className={input} />
        <input data-testid="submit-photo-email" type="email" placeholder="Email (not published)" value={form.email} onChange={set("email")} className={input} />
        <input data-testid="submit-photo-location" placeholder="Where was it taken?" value={form.location} onChange={set("location")} className={input} />
        <input data-testid="submit-photo-link" placeholder="Your website or Instagram (optional)" value={form.credit_link} onChange={set("credit_link")} className={input} />
        <input data-testid="submit-photo-caption" placeholder="Short caption" value={form.caption} onChange={set("caption")} className={`${input} sm:col-span-2`} />
        <label className="flex items-start gap-2 text-sm text-slate-600 sm:col-span-2">
          <input type="checkbox" data-testid="submit-photo-consent" checked={form.consent} onChange={() => setForm((f) => ({ ...f, consent: !f.consent }))} className="mt-0.5 h-4 w-4 accent-[var(--gold)]" />
          I took this photo and give Nassau Life permission to publish it with my credit.
        </label>
      </div>
      <button type="submit" disabled={busy} data-testid="submit-photo-button" className="mt-5 rounded-full bg-[var(--navy)] px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">
        {busy ? "Sending…" : "Send photo for review"}
      </button>
    </form>
  );
}
